import { useState } from 'react';
import { AvatarPicker } from '../../components/AvatarPicker';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ACCOUNT_KIND_DEFS,
  LEAD_RATINGS,
  LEAD_RATING_MAP,
  LEAD_STATUSES,
  LEAD_STATUS_MAP,
  normalizePhone,
} from '@goyone/shared';
import { CountrySelect } from '../../components/CountrySelect';
import { api } from '../../lib/api';
import { Modal } from '../../components/ui';
import {
  CustomFieldInputs,
  activeFields,
  toFormValue,
  toPayload,
  useCustomFields,
} from '../../components/CustomFields';

interface TextFieldDef {
  key: string;
  label: string;
  span: boolean;
  num?: boolean;
  multiline?: boolean;
}

/** الحقول الثابتة القابلة للتحرير، بترتيب ظهورها. */
const TEXT_FIELDS: readonly TextFieldDef[] = [
  { key: 'company', label: 'الشركة', span: false },
  { key: 'jobTitle', label: 'المسمى الوظيفي', span: false },
  { key: 'email', label: 'البريد الإلكتروني', span: false },
  /* معرّفا التواصل — بهما تُربط محادثات ماسنجر وإنستغرام ويُرسَل من الملف. */
  { key: 'messengerHandle', label: 'حساب ماسنجر', span: false, num: true },
  { key: 'instagramHandle', label: 'حساب إنستغرام', span: false, num: true },
  { key: 'source', label: 'المصدر', span: false },
  { key: 'industry', label: 'النشاط', span: false },
  { key: 'city', label: 'المدينة', span: false },
  { key: 'address', label: 'العنوان', span: true },
  /* رابط الخرائط — يظهر للموظف مع الاجتماعات الحضورية ليصل بلا سؤال. */
  { key: 'mapsUrl', label: 'موقع العميل (رابط خرائط)', span: true, num: true },
  { key: 'notes', label: 'ملاحظات', span: true, multiline: true },
] as const;

/*
 * نموذج العميل هو النموذج نفسه: «الشركة» تسقط (النوع والاسم يقولانها)
 * وتحلّ خصوصياته — الاسم القانوني والرقم الضريبي والموقع الإلكتروني.
 */
const ACCOUNT_TEXT_FIELDS: readonly TextFieldDef[] = [
  { key: 'legalName', label: 'الاسم القانوني', span: false },
  { key: 'taxNumber', label: 'الرقم الضريبي', span: false, num: true },
  { key: 'jobTitle', label: 'المسمى الوظيفي', span: false },
  { key: 'email', label: 'البريد الإلكتروني', span: false },
  { key: 'messengerHandle', label: 'حساب ماسنجر', span: false, num: true },
  { key: 'instagramHandle', label: 'حساب إنستغرام', span: false, num: true },
  { key: 'website', label: 'الموقع الإلكتروني', span: false, num: true },
  { key: 'source', label: 'المصدر', span: false },
  { key: 'industry', label: 'النشاط', span: false },
  { key: 'city', label: 'المدينة', span: false },
  { key: 'address', label: 'العنوان', span: true },
  { key: 'mapsUrl', label: 'موقع العميل (رابط خرائط)', span: true, num: true },
  { key: 'notes', label: 'ملاحظات', span: true, multiline: true },
] as const;

/* الهاتفان خارج القائمة العامة — لهما مفتاح دولةٍ وعلامةُ «إليه الرسائل». */
const PHONE_FIELDS = [
  { key: 'phone', label: 'الهاتف' },
  { key: 'altPhone', label: 'هاتف آخر' },
] as const;

/** يفكّ الرقم المخزَّن إلى مفتاحٍ ورقمٍ محلي — وما لا مفتاح له يُترك كما هو. */
export function splitPhone(stored: string): { code: string; local: string } {
  if (!stored.trim()) return { code: '20', local: '' };
  const parsed = normalizePhone(stored);
  if (parsed.value?.countryCode) {
    return { code: parsed.value.countryCode, local: parsed.value.national };
  }
  return { code: '', local: stored };
}

/** يركّب الرقم الكامل — المفتاح ثم المحلي بلا صفره الأول. */
export function joinPhone(code: string, local: string): string {
  const digits = local.trim();
  if (!digits) return '';
  if (!code) return digits;
  return `+${code}${digits.replace(/[\s()\-.]/g, '').replace(/^0+/, '')}`;
}

export interface LeadLike {
  id?: string;
  name?: string;
  status?: string;
  rating?: string;
  value?: unknown;
  customFields?: Record<string, unknown> | null;
  [key: string]: unknown;
}

/**
 * إنشاء عميل محتمل وتعديله.
 *
 * نموذج واحد للحالتين، كنظيره في العملاء. كان الإنشاء وحده متاحًا: تُدخَل
 * البيانات مرة ثم لا سبيل إلى تصحيحها — يُغيَّر الحالة والتصنيف بنقرة،
 * أما الهاتف الخطأ فيبقى خطأ.
 */
export function LeadEditor({
  lead,
  onClose,
  entity = 'lead',
}: {
  lead?: LeadLike;
  onClose: () => void;
  /** «ملف العميل» نموذجٌ واحد — العميل المحتمل والعميل سواء. */
  entity?: 'lead' | 'account';
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(lead?.id);
  const isAccount = entity === 'account';
  const textFields = isAccount ? ACCOUNT_TEXT_FIELDS : TEXT_FIELDS;
  /* تخصيص «ملف العميل» واحد — التعريفات مخزنها LEAD للكيانين. */
  const fields = useCustomFields('LEAD');
  const defs = activeFields(fields.data);

  const [form, setForm] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {
      name: toFormValue(lead?.name),
      status: (lead?.status as string) ?? 'NEW',
      rating: (lead?.rating as string) ?? 'WARM',
      kind: (lead?.kind as string) ?? 'COMPANY',
      value: lead?.value == null ? '' : String(Number(lead.value)),
      avatarUrl: toFormValue(lead?.avatarUrl),
    };
    for (const f of textFields) seed[f.key] = toFormValue(lead?.[f.key]);
    return seed;
  });

  /* الهاتفان مفكوكين: مفتاحٌ ورقمٌ محلي لكلٍّ — والتركيب عند الحفظ. */
  const [phones, setPhones] = useState(() => ({
    phone: splitPhone(toFormValue(lead?.phone)),
    altPhone: splitPhone(toFormValue(lead?.altPhone)),
  }));
  /* توجيه القنوات: أيُّ الرقمين يستقبل كل قناة — يُعلَّم بصندوقه. */
  const [routing, setRouting] = useState<{ wa: 'phone' | 'altPhone'; sms: 'phone' | 'altPhone' }>({
    wa: lead?.waPhone === 'altPhone' ? 'altPhone' : 'phone',
    sms: lead?.smsPhone === 'altPhone' ? 'altPhone' : 'phone',
  });

  const [custom, setCustom] = useState<Record<string, string>>(() => {
    const bag = (lead?.customFields ?? {}) as Record<string, unknown>;
    return Object.fromEntries(Object.entries(bag).map(([k, v]) => [k, toFormValue(v)]));
  });

  const set = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name: (form.name ?? '').trim(),
        /* الفارغة تُرسَل `null` لا `''`: الإزالة فعلٌ كالإضافة. */
        avatarUrl: (form.avatarUrl ?? '') || null,
        customFields: toPayload(defs, custom),
      };
      if (isAccount) {
        body.kind = form.kind;
      } else {
        body.status = form.status;
        body.rating = form.rating;
        body.value = form.value.trim() === '' ? null : Number(form.value);
      }
      for (const f of textFields) {
        const v = (form[f.key] ?? '').trim();
        body[f.key] = v === '' ? null : v;
      }

      body.phone = joinPhone(phones.phone.code, phones.phone.local) || null;
      body.altPhone = joinPhone(phones.altPhone.code, phones.altPhone.local) || null;
      body.waPhone = routing.wa;
      body.smsPhone = routing.sms;

      const base = isAccount ? '/crm/accounts' : '/crm/leads';
      return editing
        ? api(`${base}/${lead!.id}`, { method: 'PATCH', body })
        : api<{ id: string }>(base, { method: 'POST', body });
    },
    onSuccess: async () => {
      if (isAccount) {
        await queryClient.invalidateQueries({ queryKey: ['account'] });
        await queryClient.invalidateQueries({ queryKey: ['accounts'] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['lead'] });
        await queryClient.invalidateQueries({ queryKey: ['leads'] });
      }
      onClose();
    },
  });

  const valid = (form.name ?? '').trim().length >= 2;

  return (
    <Modal
      title={
        isAccount
          ? editing
            ? 'تعديل بيانات العميل'
            : 'عميل جديد'
          : editing
            ? 'تعديل بيانات العميل المحتمل'
            : 'عميل محتمل جديد'
      }
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {/*
        الصورة أعلى النموذج لا بين الحقول.
        هي أوّل ما يُرى في الجدول وفي الملف، فموضعها في النموذج يتبع
        موضعها في العرض.
      */}
      <AvatarPicker
        name={form.name ?? ''}
        value={form.avatarUrl}
        email={form.email}
        onChange={(next) => set('avatarUrl', next ?? '')}
      />

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="le-name">
            الاسم
          </label>
          <input
            id="le-name"
            className="input"
            value={form.name ?? ''}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>

        {isAccount ? (
          /* العميل نوعُه بدل مسار التأهيل — والباقي هو النموذج نفسه. */
          <div className="field">
            <label className="label" htmlFor="le-kind">
              النوع
            </label>
            <select
              id="le-kind"
              className="select"
              value={form.kind}
              onChange={(e) => set('kind', e.target.value)}
            >
              {ACCOUNT_KIND_DEFS.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div className="field">
              <label className="label" htmlFor="le-value">
                القيمة التقديرية
              </label>
              <input
                id="le-value"
                className="input num"
                type="number"
                min="0"
                value={form.value}
                onChange={(e) => set('value', e.target.value)}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="le-status">
                الحالة
              </label>
              <select
                id="le-status"
                className="select"
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
              >
                {LEAD_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {LEAD_STATUS_MAP[s].label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="label" htmlFor="le-rating">
                الأولوية
              </label>
              <select
                id="le-rating"
                className="select"
                value={form.rating}
                onChange={(e) => set('rating', e.target.value)}
              >
                {LEAD_RATINGS.map((r) => (
                  <option key={r} value={r}>
                    {LEAD_RATING_MAP[r].label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/*
          الهاتفان بمفتاح دولة: المفتاح قائمةٌ والرقم المحلي حقل — فلا
          «يلزم رقم بمفتاح دولة» بعد اليوم. والدائرة تحت كلٍّ تعلّم الرقم
          الذي تُرسَل إليه الرسائل (واتساب/SMS).
        */}
        {PHONE_FIELDS.map((f) => (
          <div key={f.key} className="field">
            <label className="label" htmlFor={`le-${f.key}`}>
              {f.label}
            </label>
            <div className="row" style={{ gap: 6 }}>
              {/* العلم وحده — والاسم في القائمة المفتوحة والتلميح. */}
              <CountrySelect
                value={phones[f.key].code}
                onChange={(code) =>
                  setPhones((p) => ({ ...p, [f.key]: { ...p[f.key], code } }))
                }
                ariaLabel={`مفتاح دولة ${f.label}`}
              />
              <input
                id={`le-${f.key}`}
                className="input num"
                dir="ltr"
                inputMode="tel"
                value={phones[f.key].local}
                onChange={(e) =>
                  setPhones((p) => ({ ...p, [f.key]: { ...p[f.key], local: e.target.value } }))
                }
                placeholder="10 1234 5678"
              />
            </div>
            {/*
              توجيه القنوات صناديق لا دائرةً واحدة: من له رقمان يقرّر
              أيُّهما يستقبل واتساب وأيُّهما يستقبل SMS — كلٌّ على حدة.
              والتعليم حصريّ داخل القناة: قناةٌ لا تذهب لرقمين.
            */}
            <span className="row tiny" style={{ gap: 'var(--sp-3)' }}>
              <label className="row" style={{ cursor: 'pointer', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={routing.wa === f.key}
                  onChange={() => setRouting((r) => ({ ...r, wa: f.key }))}
                />
                واتساب
              </label>
              <label className="row" style={{ cursor: 'pointer', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={routing.sms === f.key}
                  onChange={() => setRouting((r) => ({ ...r, sms: f.key }))}
                />
                SMS
              </label>
            </span>
          </div>
        ))}

        {textFields.map((f) => (
          <div key={f.key} className={`field${f.span ? ' span-2' : ''}`}>
            <label className="label" htmlFor={`le-${f.key}`}>
              {f.label}
            </label>
            {'multiline' in f && f.multiline ? (
              <textarea
                id={`le-${f.key}`}
                className="input"
                rows={3}
                value={form[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
              />
            ) : (
              <input
                id={`le-${f.key}`}
                className={`input${'num' in f && f.num ? ' num' : ''}`}
                value={form[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      {defs.length > 0 && (
        <>
          <h3 className="section-title mt-4">بيانات إضافية</h3>
          <div className="form-grid">
            <CustomFieldInputs
              defs={defs}
              values={custom}
              onChange={(key, value) => setCustom((c) => ({ ...c, [key]: value }))}
              idPrefix="le-cf"
            />
          </div>
        </>
      )}
    </Modal>
  );
}
